import {
  DataResult,
  State as DataState,
  FilterOperator,
} from '@progress/kendo-data-query';
import { produce } from 'immer';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { OrdersAndTransactionsTabs, formatOrders, formatTransactions } from '.';
import { getSkip } from '../../../../../../common/widgets/Grid';
import { config } from '../../../../config';
import { DropdownOptionModel } from '../../../../models/Common';
import {
  OrderItemModel,
  TransactionItemModel,
} from '../../../../models/Orders';
import { resources } from '../../../../resources/resources';
import { OrdersAndTransactionsTabNav } from './OrdersAndTransactionsTabNav';
import { OrdersGrid } from './OrdersGrid';
import { TransactionsGrid } from './TransactionsGrid';
import { OrderStatuses } from '../../../../enums';

interface FiltersData {
  period: DropdownOptionModel;
}

interface OrdersAndTransactionsState {
  activeTab: OrdersAndTransactionsTabs;
  data?: TransactionItemModel[] | OrderItemModel[];
  total: number;
  dataState: DataState;
  filtersData: FiltersData;
  showErrorPanel?: boolean;
}

const initialActiveTab = OrdersAndTransactionsTabs.Orders;

export const OrdersAndTransactions = () => {
  const { t: translate } = useTranslation();
  const periodOptions: DropdownOptionModel[] = useMemo(
    () => [
      {
        text: translate(resources.fromLastNDays, { 0: 30 }),
        value: new Date(moment().add({ days: -30 }).toString()),
      },
      {
        text: translate(resources.fromLastNDays, { 0: 90 }),
        value: new Date(moment().add({ days: -90 }).toString()),
      },
      {
        text: translate(resources.fromLastNMonths, { 0: 6 }),
        value: new Date(moment().add({ months: -6 }).toString()),
      },
      {
        text: translate(resources.fromLastYear),
        value: new Date(moment().add({ years: -1 }).toString()),
      },
      { text: translate(resources.all), value: null },
    ],
    [translate],
  );
  const initialDataState: any = useMemo(
    () => ({
      skip: 0,
      take: config.grid.defaultPageSize,
      sort: [{ field: 'dateAdded', dir: 'desc' }],
      filter: {
        logic: 'and',
        filters: [
          {
            field: 'dateAdded',
            operator: FilterOperator.GreaterThanOrEqual,
            value: periodOptions[0].value,
          },
          {
            field: 'statusId',
            operator: FilterOperator.NotEqualTo,
            value: OrderStatuses.Failed,
          },
        ],
      },
    }),
    [periodOptions],
  );
  const initialFiltersData = useMemo(
    () => ({
      period: periodOptions[0],
    }),
    [periodOptions],
  );
  const decodeOdataString = useCallback((odataString: string): DataState => {
    const newDataState = JSON.parse(decodeURI(odataString));
    newDataState.filter?.filters.forEach((f: any) => {
      if (f.field === 'dateAdded') {
        f.value = f.value ? new Date(f.value) : null;
      }
    });
    return newDataState;
  }, []);
  const decodeFiltersData = useCallback(
    (filtersDataQueryString: string): FiltersData => {
      const newFiltersData = JSON.parse(decodeURI(filtersDataQueryString));
      newFiltersData.period.value = newFiltersData.period.value
        ? new Date(newFiltersData.period.value)
        : null;
      return newFiltersData;
    },
    [],
  );
  const getActiveTab = (
    activeTabQueryString: string | null,
  ): OrdersAndTransactionsTabs => {
    if (!activeTabQueryString) return initialActiveTab;
    if (activeTabQueryString === OrdersAndTransactionsTabs.Orders)
      return OrdersAndTransactionsTabs.Orders;
    return OrdersAndTransactionsTabs.Transactions;
  };
  const [searchParams] = useSearchParams();
  const activeTabQueryString = searchParams.get('activeTab');
  const filtersDataQueryString = searchParams.get('filtersData');
  const odataString = searchParams.get('dataState');
  const navigate = useNavigate();

  // Initialize URL with query parameters if they don't exist
  const hasInitializedUrl = useRef(false);

  useEffect(() => {
    if (
      !hasInitializedUrl.current &&
      !activeTabQueryString &&
      !filtersDataQueryString &&
      !odataString
    ) {
      hasInitializedUrl.current = true;

      // Navigate to URL with initial parameters - this prevents the "null to values" change
      navigate(
        `/my-account/orders?activeTab=${initialActiveTab}&filtersData=${encodeURI(
          JSON.stringify(initialFiltersData),
        )}&dataState=${encodeURI(JSON.stringify(initialDataState))}`,
        { replace: true },
      );
      return;
    }
    hasInitializedUrl.current = true;
  }, [
    navigate,
    initialFiltersData,
    initialDataState,
    activeTabQueryString,
    filtersDataQueryString,
    odataString,
  ]);

  const prevValues = useRef({
    activeTab: activeTabQueryString,
    filtersData: filtersDataQueryString,
    odataString: odataString,
  });

  const [state, setState] = useState<OrdersAndTransactionsState>({
    activeTab: getActiveTab(activeTabQueryString),
    dataState: odataString ? decodeOdataString(odataString) : initialDataState,
    total: 0,
    filtersData: filtersDataQueryString
      ? decodeFiltersData(filtersDataQueryString)
      : initialFiltersData,
  });

  const onTabChange = (tab: OrdersAndTransactionsTabs) => {
    if (tab === state.activeTab) {
      return;
    }

    setState(
      produce(d => {
        d.data = undefined;
      }),
    );
    navigate(
      `/my-account/orders?activeTab=${tab}&filtersData=${encodeURI(
        JSON.stringify(initialFiltersData),
      )}&dataState=${encodeURI(JSON.stringify(initialDataState))}`,
    );
  };

  const onDataReceived = (result: DataResult, showErrorPanel?: boolean) => {
    setState(
      produce(d => {
        d.data =
          state.activeTab === OrdersAndTransactionsTabs.Orders
            ? formatOrders(result.data)
            : formatTransactions(result.data);
        d.total = result.total;
        d.showErrorPanel = showErrorPanel;
      }),
    );
  };

  const onDataStateChange = (dataState: DataState) => {
    navigate(
      `/my-account/orders?activeTab=${state.activeTab}&filtersData=${encodeURI(
        JSON.stringify(state.filtersData),
      )}&dataState=${encodeURI(
        JSON.stringify({
          ...dataState,
          skip: getSkip(state.dataState, dataState),
        }),
      )}`,
    );
  };

  const onPeriodChange = (period: DropdownOptionModel) => {
    const newFiltersData = { period };
    const newDataState = {
      ...state.dataState,
      skip: 0,
      filter: {
        logic: 'and' as 'and' | 'or',
        filters: [
          {
            field: 'statusId',
            operator: FilterOperator.NotEqualTo,
            value: OrderStatuses.Failed,
          },
          ...(period.value
            ? [
                {
                  field: 'dateAdded',
                  operator: FilterOperator.GreaterThanOrEqual,
                  value: period.value,
                },
              ]
            : []),
        ],
      },
    };
    setState(
      produce(d => {
        d.dataState = newDataState;
        d.filtersData.period = period;
        d.data = undefined;
      }),
    );
    navigate(
      `/my-account/orders?activeTab=${state.activeTab}&filtersData=${encodeURI(
        JSON.stringify(newFiltersData),
      )}&dataState=${encodeURI(JSON.stringify(newDataState))}`,
    );
  };

  useEffect(() => {
    if (odataString) {
      const newDataState = decodeOdataString(odataString);

      let isPaginationOnly = false;
      const prevDataStateString = prevValues.current.odataString;

      if (prevDataStateString && odataString) {
        try {
          const prevDataState = decodeOdataString(prevDataStateString);

          isPaginationOnly =
            JSON.stringify(prevDataState.sort) ===
              JSON.stringify(newDataState.sort) &&
            JSON.stringify(prevDataState.filter) ===
              JSON.stringify(newDataState.filter) &&
            (prevDataState.skip !== newDataState.skip ||
              prevDataState.take !== newDataState.take);
        } catch (e) {
          isPaginationOnly = false;
        }
      }

      setState(
        produce(d => {
          d.dataState = newDataState;
          if (!isPaginationOnly) {
            d.data = undefined;
          }
        }),
      );
    } else {
      setState(
        produce(d => {
          d.dataState = initialDataState;
        }),
      );
    }

    prevValues.current.odataString = odataString;
  }, [odataString, decodeOdataString, initialDataState]);

  useEffect(() => {
    const newActiveTab = getActiveTab(activeTabQueryString);
    const tabChanged = prevValues.current.activeTab !== activeTabQueryString;

    setState(
      produce(d => {
        d.activeTab = newActiveTab;
        if (tabChanged) {
          d.data = undefined;
        }
      }),
    );

    prevValues.current.activeTab = activeTabQueryString;
  }, [activeTabQueryString]);

  useEffect(() => {
    const filtersChanged =
      prevValues.current.filtersData !== filtersDataQueryString;

    setState(
      produce(d => {
        d.filtersData = filtersDataQueryString
          ? decodeFiltersData(filtersDataQueryString)
          : initialFiltersData;

        if (filtersChanged) {
          d.data = undefined;
        }
      }),
    );

    prevValues.current.filtersData = filtersDataQueryString;
  }, [filtersDataQueryString, decodeFiltersData, initialFiltersData]);

  // Don't render anything until URL is initialized
  if (!hasInitializedUrl.current) {
    return null;
  }

  return (
    <div
      className={`orders-transactions-container ${
        state.data && !state.data.length ? 'empty-orders-transactions' : ''
      }`}
    >
      <div className="acc-tabs-container">
        <OrdersAndTransactionsTabNav
          activeTab={state.activeTab}
          onTabChange={tab => onTabChange(tab)}
        />
      </div>
      {state.activeTab === OrdersAndTransactionsTabs.Orders && (
        <OrdersGrid
          period={state.filtersData.period}
          onPeriodChange={onPeriodChange}
          periodOptions={periodOptions}
          showFiltersAndReport
          orders={{
            data: state.data as OrderItemModel[],
            total: state.total,
            dataState: state.dataState,
            onDataReceived,
            onDataStateChange,
          }}
          showErrorPanel={state.showErrorPanel}
          isForDashboard={false}
        />
      )}
      {state.activeTab === OrdersAndTransactionsTabs.Transactions && (
        <TransactionsGrid
          period={state.filtersData.period}
          onPeriodChange={onPeriodChange}
          periodOptions={periodOptions}
          showFiltersAndReport
          transactions={{
            data: state.data as TransactionItemModel[],
            total: state.total,
            dataState: state.dataState,
            onDataReceived,
            onDataStateChange,
          }}
          showErrorPanel={state.showErrorPanel}
          isForDashboard={false}
        />
      )}
    </div>
  );
};
